import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { ApiService } from '../api/api.service';
import { HeroReportImageStorageService } from './hero-report-image-storage.service';
// import * as Sentry from '@sentry/angular';


interface HeroReportImageResponse {
  data: {
    heroReportImages: any[];
  };
}

interface HeroReportImageUpdateResponse {
  data: {
    heroReportImage: any;
  };
}

@Injectable({
  providedIn: 'root'
})

export class HeroReportImageService {
  constructor(private apiService: ApiService, private storage: HeroReportImageStorageService) {}

  /**
   * Fetches all hero report images from the server.
   *
   * This method sends a GET request to the '/hero-report-image' endpoint
   * and returns the list of hero report images.
   *
   * @returns {Promise<Array>} A promise that resolves with an array of hero report images.
   * @throws {Error} Throws an error if the request fails.
   */
  async fetch(): Promise<Array<any>> {
    try {
      const response = await this.apiService.get<HeroReportImageResponse>('/hero-report-image');
      return response?.data?.heroReportImages || [];
    } catch (error) {
      // Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Creates a new hero report image.
   *
   * This method sends a POST request with the hero report image data to the '/hero-report-image' endpoint
   * and returns the created hero report image data.
   *
   * @param {Object} image - The hero report image data to create.
   * @returns {Promise<Object>} A promise that resolves with the created hero report image data.
   * @throws {Error} Throws an error if the request fails.
   */
  async create(image: any): Promise<any> {
    try {
      const response = await this.apiService.post<HeroReportImageUpdateResponse>('/hero-report-image', image);
      return response?.data?.heroReportImage;
    } catch (error) {
      // Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Updates an existing hero report image.
   *
   * This method sends a PUT request with the hero report image data to the '/hero-report-image/{id}' endpoint,
   * where {id} is the ID of the hero report image to update. It returns the updated hero report image data.
   *
   * @param {Object} image - The hero report image data to update, including the ID.
   * @returns {Promise<Object>} A promise that resolves with the updated hero report image data.
   * @throws {Error} Throws an error if the request fails.
   */
  async update(image: any): Promise<any> {
    try {
      const response = await this.apiService.put<HeroReportImageUpdateResponse>(`/hero-report-image/${image.id}`, image);
      return response.data.heroReportImage;
    } catch (error) {
      console.log(error, error.response);
      // Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Changes the caption and/or display order of an image. Nothing else about an image can be changed
   * once it exists; a blank caption clears it.
   */
  async updateMetadata(id: string, metadata: { caption: string | null; displayOrder: number }): Promise<any> {
    const response = await this.apiService.put<HeroReportImageUpdateResponse>(`/hero-report-image/${id}`, metadata);
    return response.data.heroReportImage;
  }

  /**
   * Deletes an image record.
   */
  async delete(id: string): Promise<void> {
    await this.apiService.delete(`/hero-report-image/${id}`);
  }

  /**
   * Creates a new hero report image and uploads the image to S3 with federated credentials.
   *
   * The image record is created first, so hero-api can issue its S3 key. The image's `id` is
   * part of that key and the only thing keeping other users' images unguessable, so it is always
   * a random UUID v4 (the `uuid` package draws from crypto.getRandomValues) generated here.
   *
   * @param {Object} image - The hero report image data to create. Must include 'url' (data URL) and 'type'.
   * @returns {Promise<Object>} A promise that resolves with the created hero report image data.
   */
  async createAndUpload(image: { url: string, type: string, [key: string]: any }): Promise<any> {
    const { url, ...meta } = image;
    const created = await this.create({ ...meta, id: meta['id'] || uuidv4() });

    if (url && url.startsWith('data:')) {
      await this.storage.upload(created, this.dataURLtoBlob(url));
    }
    return created;
  }

  /**
   * Converts a data URL to a Blob for uploading.
   */
  private dataURLtoBlob(dataurl: string): Blob {
    const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  }
}
